import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  MenuItem,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import {
  getGame,
  saveGameSettings,
  saveKnowledgeBaseItems,
  loadKbVideo,
  loadPoster,
  getProfile,
} from "../../redux/asyncThunk/userActions";
import { sanitizePlainText } from "../../utils/inputValidation";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import ProjectMessengerAvailability, {
  readAvailableMessengersFromSettings,
} from "../ProjectMessengerAvailability";
import { AUTH_FIELD_SX } from "../../pages/AuthLayout";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import "../trainer/trainer-editor.css";
import "./knowledge-base-editor.css";

const { addMessage } = userSlice;
const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

function createEmptyItem() {
  const id = `kb-${Date.now()}`;
  return {
    id,
    question: "",
    answerMode: "text",
    answerText: "",
    hasVideo: false,
    screenpath: "",
  };
}

function formatQuestionCount(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} вопросов`;
  if (mod10 === 1) return `${n} вопрос`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} вопроса`;
  return `${n} вопросов`;
}

export default function KnowledgeBaseEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  const [kbTheme, setKbTheme] = useState(game?.kbTheme ?? game?.title ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [items, setItems] = useState(game?.kbItems ?? []);
  const [activeItemId, setActiveItemId] = useState(null);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [view, setView] = useState("editor");
  const [availableMessengers, setAvailableMessengers] = useState(
    readAvailableMessengersFromSettings(game?.settings),
  );

  const itemsSaveTimerRef = useRef(null);
  const settingsSaveTimerRef = useRef(null);
  const lastSavedItemsRef = useRef(JSON.stringify(game?.kbItems ?? []));
  const lastSavedSettingsRef = useRef("");

  const activeItem = useMemo(
    () => items.find((item) => item.id === activeItemId) ?? null,
    [items, activeItemId],
  );

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  const buildSettingsSnapshot = useCallback(
    () =>
      JSON.stringify({
        kbTheme: kbTheme.trim(),
        description,
        settings,
        availableMessengers,
      }),
    [kbTheme, description, settings, availableMessengers],
  );

  useEffect(() => {
    if (!game) return;
    const nextTheme = game.kbTheme ?? game.title ?? "";
    const nextItems = game.kbItems ?? [];
    setKbTheme(nextTheme);
    setDescription(game.description ?? "");
    setItems(nextItems);
    setSettings({
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    });
    setAvailableMessengers(readAvailableMessengersFromSettings(game.settings));
    lastSavedItemsRef.current = JSON.stringify(nextItems);
    lastSavedSettingsRef.current = JSON.stringify({
      kbTheme: nextTheme.trim(),
      description: game.description ?? "",
      settings: {
        projectStatus: getProjectStatusFromSettings(game.settings),
        completion_text: game.settings?.completion_text ?? "",
        author_telegram: game.settings?.author_telegram ?? "",
        allow_replay: game.settings?.allow_replay !== false,
      },
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
    });
  }, [game]);

  const persistItems = useCallback(
    async (itemsToSave) => {
      if (!game?._id) return;
      const trimmedTheme = kbTheme.trim();
      try {
        await dispatch(
          saveKnowledgeBaseItems({
            gameId: game._id,
            kbTheme: trimmedTheme,
            items: itemsToSave,
          }),
        ).unwrap();
        lastSavedItemsRef.current = JSON.stringify(itemsToSave);
      } catch {
        dispatch(addMessage({ type: "error", message: "Не удалось сохранить вопросы" }));
      }
    },
    [dispatch, game?._id, kbTheme],
  );

  const persistSettings = useCallback(async () => {
    if (!game?._id) return;
    const trimmedTheme = kbTheme.trim();
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: trimmedTheme,
          description,
          program: trimmedTheme,
          category: "",
          kbTheme: trimmedTheme,
          settings: mergeGameSettings(game?.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),
            contentType: "knowledge_base",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
            availableMessengers,
          }),
        }),
      ).unwrap();
      lastSavedSettingsRef.current = buildSettingsSnapshot();
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить настройки" }));
    }
  }, [
    dispatch,
    game?._id,
    game?.settings,
    kbTheme,
    description,
    settings,
    availableMessengers,
    buildSettingsSnapshot,
  ]);

  useEffect(() => {
    const serialized = JSON.stringify(items);
    if (serialized === lastSavedItemsRef.current || !game?._id) return;

    clearTimeout(itemsSaveTimerRef.current);
    itemsSaveTimerRef.current = setTimeout(() => {
      persistItems(items);
    }, 800);

    return () => clearTimeout(itemsSaveTimerRef.current);
  }, [items, game?._id, persistItems]);

  useEffect(() => {
    const serialized = buildSettingsSnapshot();
    if (serialized === lastSavedSettingsRef.current || !game?._id) return;

    clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      persistSettings();
    }, 800);

    return () => clearTimeout(settingsSaveTimerRef.current);
  }, [
    kbTheme,
    description,
    settings,
    availableMessengers,
    game?._id,
    buildSettingsSnapshot,
    persistSettings,
  ]);

  const updateActiveItem = (patch) => {
    if (!activeItemId) return;
    setItems((prev) =>
      prev.map((item) => (item.id === activeItemId ? { ...item, ...patch } : item)),
    );
  };

  const handleAddItem = async () => {
    clearTimeout(itemsSaveTimerRef.current);
    await persistItems(items);
    const next = createEmptyItem();
    setItems((prev) => [...prev, next]);
    setActiveItemId(next.id);
  };

  const handleViewChange = async (nextView) => {
    if (view === "settings" && nextView !== "settings") {
      clearTimeout(settingsSaveTimerRef.current);
      await persistSettings();
    }
    setView(nextView);
  };

  const handleDeleteItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    if (activeItemId === itemId) {
      setActiveItemId(null);
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeItem || !user?.id || !game?._id) return;

    dispatch(
      loadKbVideo({
        userId: user.id,
        gameId: game._id,
        itemId: activeItem.id,
        file,
      }),
    ).then((result) => {
      if (result.payload?.kbItems) {
        lastSavedItemsRef.current = JSON.stringify(result.payload.kbItems);
        setItems(result.payload.kbItems);
      }
    });
  };

  const handlePoster = (event, gameId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId })),
        }),
      );
    };
  };

  const renderSettingsPanel = () => (
    <section className="trainer-editor__card trainer-editor__card--settings">
      <h3>Настройки темы</h3>
      <TextField
        label="Название темы"
        size="small"
        fullWidth
        sx={fieldSx}
        value={kbTheme}
        onChange={(e) => setKbTheme(sanitizePlainText(e.target.value, 200))}
        placeholder="Например: адаптация новых сотрудников"
      />
      <TextField
        label="Приветственный текст"
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={fieldSx}
        value={description}
        onChange={(e) =>
          setDescription(sanitizePlainText(e.target.value, 2000))
        }
      />
      <div className="trainer-editor__settings-row">
        <TextField
          select
          label="Статус"
          size="small"
          sx={{ ...fieldSx, flex: 1 }}
          value={settings.projectStatus}
          onChange={(e) =>
            setSettings((prev) => ({
              ...prev,
              projectStatus: e.target.value,
            }))
          }
        >
          <MenuItem value="draft">Черновик</MenuItem>
          <MenuItem value="published">Опубликован</MenuItem>
          <MenuItem value="hidden">Скрыт</MenuItem>
        </TextField>
        <TextField
          label="Telegram HR"
          size="small"
          sx={{ ...fieldSx, flex: 1.4 }}
          value={settings.author_telegram}
          onChange={(e) =>
            setSettings((prev) => ({
              ...prev,
              author_telegram: sanitizePlainText(e.target.value, 80),
            }))
          }
        />
      </div>
      <div className="kb-editor__poster">
        <InputFileUpload
          label="Обложка"
          accept="image/*"
          onChange={(event) => handlePoster(event, game._id)}
        />
      </div>
      <ProjectMessengerAvailability
        gameId={game._id}
        value={availableMessengers}
        onChange={setAvailableMessengers}
        compact
        showShareLinks
        showShareGuide={false}
      />
    </section>
  );

  const renderToolbar = () => (
    <div className="trainer-editor__toolbar trainer-editor__toolbar--tabs">
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          view === "editor" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<EditOutlinedIcon />}
        onClick={() => handleViewChange("editor")}
      >
        Редактор
      </Button>
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          view === "settings" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<SettingsOutlinedIcon />}
        onClick={() => handleViewChange("settings")}
      >
        Настройки
      </Button>
    </div>
  );

  const renderMetaBar = () => (
    <div className="trainer-editor__meta-bar">
      <span className="trainer-editor__meta-theme">
        Тема: <strong>{kbTheme.trim() || "Без названия"}</strong>
      </span>
      {view === "editor" ? (
        <span className="trainer-editor__meta-count">
          {formatQuestionCount(items.length)}
        </span>
      ) : null}
    </div>
  );

  return (
    <div className="trainer-editor kb-editor">
      {renderToolbar()}
      {renderMetaBar()}

      {view === "settings" ? (
        <div className="trainer-editor__workspace trainer-editor__workspace--settings kb-editor__workspace--settings">
          {renderSettingsPanel()}
        </div>
      ) : (
        <div className="trainer-editor__workspace kb-editor__workspace">
          <section className="trainer-editor__card trainer-editor__card--questions">
            <div className="trainer-editor__card-head">
              <div>
                <h3>Вопросы и ответы</h3>
                <p className="trainer-editor__card-lead">
                  Наполните тему типовыми ситуациями — сотрудник выберет вопрос в
                  боте и получит ответ.
                </p>
              </div>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
            >
              Добавить вопрос
            </Button>
          </div>

          <div className="trainer-editor__questions-layout">
            <div className="trainer-editor__questions-nav">
              {items.length === 0 ? (
                <p className="trainer-editor__empty kb-editor__empty">
                  Добавьте первый вопрос — текстом или видео-ответом.
                </p>
              ) : (
                <ul className="trainer-editor__question-list kb-editor__list">
                  {items.map((item, index) => (
                    <li
                      key={item.id}
                      className={`trainer-editor__question-item kb-editor__list-item${
                        activeItemId === item.id
                          ? " trainer-editor__question-item--active kb-editor__list-item--active"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="trainer-editor__question-btn kb-editor__list-btn"
                        onClick={() => setActiveItemId(item.id)}
                      >
                        <span className="trainer-editor__question-num">
                          {index + 1}
                        </span>
                        <MenuBookOutlinedIcon fontSize="small" />
                        {item.question || "Без названия"}
                      </button>
                      <IconButton
                        size="small"
                        aria-label="Удалить"
                        onClick={() => handleDeleteItem(item.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="trainer-editor__questions-editor">
              {activeItem ? (
                <div className="trainer-editor__question-form">
                  <TextField
                    label="Вопрос сотрудника"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={fieldSx}
                    value={activeItem.question}
                    onChange={(e) =>
                      updateActiveItem({
                        question: sanitizePlainText(e.target.value, 500),
                      })
                    }
                    placeholder="Например: Как оформить отпуск?"
                  />

                  <span className="trainer-editor__answers-label">
                    Формат ответа
                  </span>
                  <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={activeItem.answerMode}
                    onChange={(_, value) => {
                      if (value) updateActiveItem({ answerMode: value });
                    }}
                    className="app-dialog__type-toggle"
                  >
                    <ToggleButton value="text">
                      <TextFieldsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Текст
                    </ToggleButton>
                    <ToggleButton value="video">
                      <VideocamOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Видео
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {activeItem.answerMode === "text" ? (
                    <TextField
                      label="Ответ"
                      size="small"
                      fullWidth
                      multiline
                      minRows={5}
                      sx={fieldSx}
                      value={activeItem.answerText}
                      onChange={(e) =>
                        updateActiveItem({
                          answerText: sanitizePlainText(e.target.value, 4000),
                        })
                      }
                      placeholder="Текст ответа, который увидит сотрудник в боте"
                    />
                  ) : (
                    <div className="kb-editor__video-upload">
                      <InputFileUpload
                        label="Видео-ответ"
                        accept="video/*"
                        onChange={handleVideoUpload}
                      />
                      {activeItem.hasVideo ? (
                        <p className="kb-editor__video-status">Видео загружено</p>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : (
                <div className="trainer-editor__questions-placeholder">
                  Выберите вопрос слева или добавьте новый.
                </div>
              )}
            </div>
          </div>
        </section>
        </div>
      )}
    </div>
  );
}
